import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Grid, TextField, CircularProgress, Button, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ClearIcon from '@mui/icons-material/Clear';
import CasinoIcon from '@mui/icons-material/Casino';
import useTeamCompetitionData from './useTeamCompetitionData';
import NavLink from './common/NavLink';
import { slugify } from '../utils/slugify';

const COMPETITION_ID = 431;
const GROUPS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];

const getWorldCupGroupFromMatch = (match) => {
  const round = (match.round || '').toString().trim().toUpperCase();
  const roundMatch = round.match(/^GRUPA\s+([A-L])(?:\b|$)/);
  if (roundMatch) return roundMatch[1];

  const group = (match.group || '').toString().trim().toUpperCase();
  const groupMatch = group.match(/^(?:GRUPA\s+)?([A-L])$/);
  return groupMatch ? groupMatch[1] : '';
};

const getWorldCupKnockoutStage = (match) => {
  const round = (match.round || '').toString().trim().toUpperCase();
  if (round.includes('1/16')) return 'r32';
  if (round.includes('1/8')) return 'r16';
  if (round.includes('1/4')) return 'qf';
  if (round.includes('1/2')) return 'sf';
  if (round.includes('MECZ O 3')) return 'third';
  if (round.includes('FINA')) return 'final';
  return '';
};

const teamPairKey = (teamAId, teamBId) => {
  const ids = [Number(teamAId), Number(teamBId)].sort((a, b) => a - b);
  return `${ids[0]}_${ids[1]}`;
};

const getPenaltyWinnerId = (match) => (
  match.penaltyWinnerId ??
  match.penaltyWinnerTeamId ??
  match.penalty_winner_id ??
  match.penaltyWinner ??
  null
);



// MAPA 495 KOMBINACJI - TUTAJ WKLEJ SWOJE DANE Z JAVY
// Kluczem jest alfabetycznie posortowany ciąg 8 liter grup, które awansowały z 3. miejsca.
// Mapowanie na przeciwników: [1A, 1B, 1D, 1E, 1G, 1I, 1K, 1L]
const THIRD_PLACE_RULES = {
  "EFGHIJKL": ["E", "J", "I", "F", "H", "G", "L", "K"],
  "DFGHIJKL": ["H", "G", "I", "D", "J", "F", "L", "K"],
  "DEGHIJKL": ["E", "J", "I", "D", "H", "G", "L", "K"],
  "DEFHIJKL": ["E", "J", "I", "D", "H", "F", "L", "K"],
  "DEFGIJKL": ["E", "G", "I", "D", "J", "F", "L", "K"],
  "DEFGHJKL": ["E", "G", "J", "D", "H", "F", "L", "K"],
  "DEFGHIKL": ["E", "G", "I", "D", "H", "F", "L", "K"],
  "DEFGHIJL": ["E", "G", "J", "D", "H", "F", "L", "I"],
  "DEFGHIJK": ["E", "G", "J", "D", "H", "F", "I", "K"],
  "CFGHIJKL": ["H", "G", "I", "C", "J", "F", "L", "K"],
  "CEGHIJKL": ["E", "J", "I", "C", "H", "G", "L", "K"],
  "CEFHIJKL": ["E", "J", "I", "C", "H", "F", "L", "K"],
  "CEFGIJKL": ["E", "G", "I", "C", "J", "F", "L", "K"],
  "CEFGHJKL": ["E", "G", "J", "C", "H", "F", "L", "K"],
  "CEFGHIKL": ["E", "G", "I", "C", "H", "F", "L", "K"],
  "CEFGHIJL": ["E", "G", "J", "C", "H", "F", "L", "I"],
  "CEFGHIJK": ["E", "G", "J", "C", "H", "F", "I", "K"],
  "CDGHIJKL": ["H", "G", "I", "C", "J", "D", "L", "K"],
  "CDFHIJKL": ["C", "J", "I", "D", "H", "F", "L", "K"],
  "CDFGIJKL": ["C", "G", "I", "D", "J", "F", "L", "K"],
  "CDFGHJKL": ["C", "G", "J", "D", "H", "F", "L", "K"],
  "CDFGHIKL": ["C", "G", "I", "D", "H", "F", "L", "K"],
  "CDFGHIJL": ["C", "G", "J", "D", "H", "F", "L", "I"],
  "CDFGHIJK": ["C", "G", "J", "D", "H", "F", "I", "K"],
  "CDEHIJKL": ["E", "J", "I", "C", "H", "D", "L", "K"],
  "CDEGIJKL": ["E", "G", "I", "C", "J", "D", "L", "K"],
  "CDEGHJKL": ["E", "G", "J", "C", "H", "D", "L", "K"],
  "CDEGHIKL": ["E", "G", "I", "C", "H", "D", "L", "K"],
  "CDEGHIJL": ["E", "G", "J", "C", "H", "D", "L", "I"],
  "CDEGHIJK": ["E", "G", "J", "C", "H", "D", "I", "K"],
  "CDEFIJKL": ["C", "J", "E", "D", "I", "F", "L", "K"],
  "CDEFHJKL": ["C", "J", "E", "D", "H", "F", "L", "K"],
  "CDEFHIKL": ["C", "E", "I", "D", "H", "F", "L", "K"],
  "CDEFHIJL": ["C", "J", "E", "D", "H", "F", "L", "I"],
  "CDEFHIJK": ["C", "J", "E", "D", "H", "F", "I", "K"],
  "CDEFGJKL": ["C", "G", "E", "D", "J", "F", "L", "K"],
  "CDEFGIKL": ["C", "G", "E", "D", "I", "F", "L", "K"],
  "CDEFGIJL": ["C", "G", "E", "D", "J", "F", "L", "I"],
  "CDEFGIJK": ["C", "G", "E", "D", "J", "F", "I", "K"],
  "CDEFGHKL": ["C", "G", "E", "D", "H", "F", "L", "K"],
  "CDEFGHJL": ["C", "G", "J", "D", "H", "F", "L", "E"],
  "CDEFGHJK": ["C", "G", "J", "D", "H", "F", "E", "K"],
  "CDEFGHIL": ["C", "G", "E", "D", "H", "F", "L", "I"],
  "CDEFGHIK": ["C", "G", "E", "D", "H", "F", "I", "K"],
  "CDEFGHIJ": ["C", "G", "J", "D", "H", "F", "E", "I"],
  "BFGHIJKL": ["H", "J", "B", "F", "I", "G", "L", "K"],
  "BEGHIJKL": ["E", "J", "I", "B", "H", "G", "L", "K"],
  "BEFHIJKL": ["E", "J", "B", "F", "I", "H", "L", "K"],
  "BEFGIJKL": ["E", "J", "B", "F", "I", "G", "L", "K"],
  "BEFGHJKL": ["E", "J", "B", "F", "H", "G", "L", "K"],
  "BEFGHIKL": ["E", "G", "B", "F", "I", "H", "L", "K"],
  "BEFGHIJL": ["E", "J", "B", "F", "H", "G", "L", "I"],
  "BEFGHIJK": ["E", "J", "B", "F", "H", "G", "I", "K"],
  "BDGHIJKL": ["H", "J", "B", "D", "I", "G", "L", "K"],
  "BDFHIJKL": ["H", "J", "B", "D", "I", "F", "L", "K"],
  "BDFGIJKL": ["I", "G", "B", "D", "J", "F", "L", "K"],
  "BDFGHJKL": ["H", "G", "B", "D", "J", "F", "L", "K"],
  "BDFGHIKL": ["H", "G", "B", "D", "I", "F", "L", "K"],
  "BDFGHIJL": ["H", "G", "B", "D", "J", "F", "L", "I"],
  "BDFGHIJK": ["H", "G", "B", "D", "J", "F", "I", "K"],
  "BDEHIJKL": ["E", "J", "B", "D", "I", "H", "L", "K"],
  "BDEGIJKL": ["E", "J", "B", "D", "I", "G", "L", "K"],
  "BDEGHJKL": ["E", "J", "B", "D", "H", "G", "L", "K"],
  "BDEGHIKL": ["E", "G", "B", "D", "I", "H", "L", "K"],
  "BDEGHIJL": ["E", "J", "B", "D", "H", "G", "L", "I"],
  "BDEGHIJK": ["E", "J", "B", "D", "H", "G", "I", "K"],
  "BDEFHIJL": ["E", "J", "B", "D", "H", "F", "L", "I"],
  "BDEFHIJK": ["E", "J", "B", "D", "H", "F", "I", "K"],
  "BDEFGJKL": ["E", "G", "B", "D", "J", "F", "L", "K"],
  "BDEFGIKL": ["E", "G", "B", "D", "I", "F", "L", "K"],
  "BDEFGIJL": ["E", "G", "B", "D", "J", "F", "L", "I"],
  "BDEFGIJK": ["E", "G", "B", "D", "J", "F", "I", "K"],
  "BDEFGHKL": ["E", "G", "B", "D", "H", "F", "L", "K"],
  "BDEFGHJL": ["H", "G", "B", "D", "J", "F", "L", "E"],
  "BDEFGHJK": ["H", "G", "B", "D", "J", "F", "E", "K"],
  "BDEFGHIL": ["E", "G", "B", "D", "H", "F", "L", "I"],
  "BDEFGHIK": ["E", "G", "B", "D", "H", "F", "I", "K"],
  "BDEFGHIJ": ["H", "G", "B", "D", "J", "F", "E", "I"],
  "BCGHIJKL": ["H", "J", "B", "C", "I", "G", "L", "K"],
  "BCFHIJKL": ["H", "J", "B", "C", "I", "F", "L", "K"],
  "BCFGIJKL": ["I", "G", "B", "C", "J", "F", "L", "K"],
  "BCFGHJKL": ["H", "G", "B", "C", "J", "F", "L", "K"],
  "BCFGHIKL": ["H", "G", "B", "C", "I", "F", "L", "K"],
  "BCFGHIJL": ["H", "G", "B", "C", "J", "F", "L", "I"],
  "BCFGHIJK": ["H", "G", "B", "C", "J", "F", "I", "K"],
  "BCEHIJKL": ["E", "J", "B", "C", "I", "H", "L", "K"],
  "BCEGIJKL": ["E", "J", "B", "C", "I", "G", "L", "K"],
  "BCEGHJKL": ["E", "J", "B", "C", "H", "G", "L", "K"],
  "BCEGHIKL": ["E", "G", "B", "C", "I", "H", "L", "K"],
  "BCEGHIJL": ["E", "J", "B", "C", "H", "G", "L", "I"],
  "BCEGHIJK": ["E", "J", "B", "C", "H", "G", "I", "K"],
  "BCEFHIJL": ["E", "J", "B", "C", "H", "F", "L", "I"],
  "BCEFHIJK": ["E", "J", "B", "C", "H", "F", "I", "K"],
  "BCEFGJKL": ["E", "G", "B", "C", "J", "F", "L", "K"],
  "BCEFGIKL": ["E", "G", "B", "C", "I", "F", "L", "K"],
  "BCEFGIJL": ["E", "G", "B", "C", "J", "F", "L", "I"],
  "BCEFGIJK": ["E", "G", "B", "C", "J", "F", "I", "K"],
  "BCEFGHKL": ["E", "G", "B", "C", "H", "F", "L", "K"],
  "BCEFGHJL": ["H", "G", "B", "C", "J", "F", "L", "E"],
  "BCEFGHJK": ["H", "G", "B", "C", "J", "F", "E", "K"],
  "BCEFGHIL": ["E", "G", "B", "C", "H", "F", "L", "I"],
  "BCEFGHIK": ["E", "G", "B", "C", "H", "F", "I", "K"],
  "BCEFGHIJ": ["H", "G", "B", "C", "J", "F", "E", "I"],
  "BCDGHIJL": ["H", "G", "B", "C", "J", "D", "L", "I"],
  "BCDGHIJK": ["H", "G", "B", "C", "J", "D", "I", "K"],
  "BCDFIJKL": ["C", "J", "B", "D", "I", "F", "L", "K"],
  "BCDFHJKL": ["C", "J", "B", "D", "H", "F", "L", "K"],
  "BCDFHIKL": ["C", "I", "B", "D", "H", "F", "L", "K"],
  "BCDFHIJL": ["C", "J", "B", "D", "H", "F", "L", "I"],
  "BCDFHIJK": ["C", "J", "B", "D", "H", "F", "I", "K"],
  "BCDFGJKL": ["C", "G", "B", "D", "J", "F", "L", "K"],
  "BCDFGIKL": ["C", "G", "B", "D", "I", "F", "L", "K"],
  "BCDFGIJL": ["C", "G", "B", "D", "J", "F", "L", "I"],
  "BCDFGIJK": ["C", "G", "B", "D", "J", "F", "I", "K"],
  "BCDFGHKL": ["C", "G", "B", "D", "H", "F", "L", "K"],
  "BCDFGHJL": ["C", "G", "B", "D", "H", "F", "L", "J"],
  "BCDFGHJK": ["H", "G", "B", "C", "J", "F", "D", "K"],
  "BCDFGHIL": ["C", "G", "B", "D", "H", "F", "L", "I"],
  "BCDFGHIK": ["C", "G", "B", "D", "H", "F", "I", "K"],
  "BCDFGHIJ": ["H", "G", "B", "C", "J", "F", "D", "I"],
  "BCDEIJKL": ["E", "J", "B", "C", "I", "D", "L", "K"],
  "BCDEHJKL": ["E", "J", "B", "C", "H", "D", "L", "K"],
  "BCDEHIKL": ["E", "I", "B", "C", "H", "D", "L", "K"],
  "BCDEHIJL": ["E", "J", "B", "C", "H", "D", "L", "I"],
  "BCDEHIJK": ["E", "J", "B", "C", "H", "D", "I", "K"],
  "BCDEGJKL": ["E", "G", "B", "C", "J", "D", "L", "K"],
  "BCDEGIKL": ["E", "G", "B", "C", "I", "D", "L", "K"],
  "BCDEGIJL": ["E", "G", "B", "C", "J", "D", "L", "I"],
  "BCDEGIJK": ["E", "G", "B", "C", "J", "D", "I", "K"],
  "BCDEGHKL": ["E", "G", "B", "C", "H", "D", "L", "K"],
  "BCDEGHJL": ["H", "G", "B", "C", "J", "D", "L", "E"],
  "BCDEGHJK": ["H", "G", "B", "C", "J", "D", "E", "K"],
  "BCDEGHIL": ["E", "G", "B", "C", "H", "D", "L", "I"],
  "BCDEGHIK": ["E", "G", "B", "C", "H", "D", "I", "K"],
  "BCDEGHIJ": ["H", "G", "B", "C", "J", "D", "E", "I"],
  "BCDEFJKL": ["C", "J", "B", "D", "E", "F", "L", "K"],
  "BCDEFIKL": ["C", "E", "B", "D", "I", "F", "L", "K"],
  "BCDEFIJL": ["C", "J", "B", "D", "E", "F", "L", "I"],
  "BCDEFIJK": ["C", "J", "B", "D", "E", "F", "I", "K"],
  "BCDEFHKL": ["C", "E", "B", "D", "H", "F", "L", "K"],
  "BCDEFHJL": ["C", "J", "B", "D", "H", "F", "L", "E"],
  "BCDEFHJK": ["C", "J", "B", "D", "H", "F", "E", "K"],
  "BCDEFHIL": ["C", "E", "B", "D", "H", "F", "L", "I"],
  "BCDEFHIK": ["C", "E", "B", "D", "H", "F", "I", "K"],
  "BCDEFHIJ": ["C", "J", "B", "D", "H", "F", "E", "I"],
  "BCDEFGKL": ["C", "G", "B", "D", "E", "F", "L", "K"],
  "BCDEFGJL": ["C", "G", "B", "D", "J", "F", "L", "E"],
  "BCDEFGJK": ["C", "G", "B", "D", "J", "F", "E", "K"],
  "BCDEFGIL": ["C", "G", "B", "D", "E", "F", "L", "I"],
  "BCDEFGIK": ["C", "G", "B", "D", "E", "F", "I", "K"],
  "BCDEFGIJ": ["C", "G", "B", "D", "J", "F", "E", "I"],
  "BCDEFGHL": ["C", "G", "B", "D", "H", "F", "L", "E"],
  "BCDEFGHK": ["C", "G", "B", "D", "H", "F", "E", "K"],
  "BCDEFGHJ": ["H", "G", "B", "C", "J", "F", "D", "E"],
  "BCDEFGHI": ["C", "G", "B", "D", "H", "F", "E", "I"],
  "AFGHIJKL": ["H", "J", "I", "F", "A", "G", "L", "K"],
  "AEGHIJKL": ["E", "J", "I", "A", "H", "G", "L", "K"],
  "AEFHIJKL": ["E", "J", "I", "F", "A", "H", "L", "K"],
  "AEFGIJKL": ["E", "J", "I", "F", "A", "G", "L", "K"],
  "AEFGHJKL": ["E", "G", "J", "F", "A", "H", "L", "K"],
  "AEFGHIKL": ["E", "G", "I", "F", "A", "H", "L", "K"],
  "AEFGHIJL": ["E", "G", "J", "F", "A", "H", "L", "I"],
  "AEFGHIJK": ["E", "G", "J", "F", "A", "H", "I", "K"],
  "ADGHIJKL": ["H", "J", "I", "D", "A", "G", "L", "K"],
  "ADFHIJKL": ["H", "J", "I", "D", "A", "F", "L", "K"],
  "ADFGIJKL": ["I", "G", "J", "D", "A", "F", "L", "K"],
  "ADFGHJKL": ["H", "G", "J", "D", "A", "F", "L", "K"],
  "ADFGHIKL": ["H", "G", "I", "D", "A", "F", "L", "K"],
  "ADFGHIJL": ["H", "G", "J", "D", "A", "F", "L", "I"],
  "ADFGHIJK": ["H", "G", "J", "D", "A", "F", "I", "K"],
  "ADEHIJKL": ["E", "J", "I", "D", "A", "H", "L", "K"],
  "ADEGIJKL": ["E", "J", "I", "D", "A", "G", "L", "K"],
  "ADEGHJKL": ["E", "G", "J", "D", "A", "H", "L", "K"],
  "ADEGHIKL": ["E", "G", "I", "D", "A", "H", "L", "K"],
  "ADEGHIJL": ["E", "G", "J", "D", "A", "H", "L", "I"],
  "ADEGHIJK": ["E", "G", "J", "D", "A", "H", "I", "K"],
  "ADEFHIJL": ["H", "J", "E", "D", "A", "F", "L", "I"],
  "ADEFHIJK": ["H", "J", "E", "D", "A", "F", "I", "K"],
  "ADEFGJKL": ["E", "G", "J", "D", "A", "F", "L", "K"],
  "ADEFGIKL": ["E", "G", "I", "D", "A", "F", "L", "K"],
  "ADEFGIJL": ["E", "G", "J", "D", "A", "F", "L", "I"],
  "ADEFGIJK": ["E", "G", "J", "D", "A", "F", "I", "K"],
  "ADEFGHKL": ["H", "G", "E", "D", "A", "F", "L", "K"],
  "ADEFGHJL": ["H", "G", "J", "D", "A", "F", "L", "E"],
  "ADEFGHJK": ["H", "G", "J", "D", "A", "F", "E", "K"],
  "ADEFGHIL": ["H", "G", "E", "D", "A", "F", "L", "I"],
  "ADEFGHIK": ["H", "G", "E", "D", "A", "F", "I", "K"],
  "ADEFGHIJ": ["H", "G", "J", "D", "A", "F", "E", "I"],
  "ACGHIJKL": ["H", "J", "I", "C", "A", "G", "L", "K"],
  "ACFHIJKL": ["H", "J", "I", "C", "A", "F", "L", "K"],
  "ACFGIJKL": ["I", "G", "J", "C", "A", "F", "L", "K"],
  "ACFGHJKL": ["H", "G", "J", "C", "A", "F", "L", "K"],
  "ACFGHIKL": ["H", "G", "I", "C", "A", "F", "L", "K"],
  "ACFGHIJL": ["H", "G", "J", "C", "A", "F", "L", "I"],
  "ACFGHIJK": ["H", "G", "J", "C", "A", "F", "I", "K"],
  "ACEHIJKL": ["E", "J", "I", "C", "A", "H", "L", "K"],
  "ACEGIJKL": ["E", "J", "I", "C", "A", "G", "L", "K"],
  "ACEGHJKL": ["E", "G", "J", "C", "A", "H", "L", "K"],
  "ACEGHIKL": ["E", "G", "I", "C", "A", "H", "L", "K"],
  "ACEGHIJL": ["E", "G", "J", "C", "A", "H", "L", "I"],
  "ACEGHIJK": ["E", "G", "J", "C", "A", "H", "I", "K"],
  "ACEFHIJL": ["H", "J", "E", "C", "A", "F", "L", "I"],
  "ACEFHIJK": ["H", "J", "E", "C", "A", "F", "I", "K"],
  "ACEFGJKL": ["E", "G", "J", "C", "A", "F", "L", "K"],
  "ACEFGIKL": ["E", "G", "I", "C", "A", "F", "L", "K"],
  "ACEFGIJL": ["E", "G", "J", "C", "A", "F", "L", "I"],
  "ACEFGIJK": ["E", "G", "J", "C", "A", "F", "I", "K"],
  "ACEFGHKL": ["H", "G", "E", "C", "A", "F", "L", "K"],
  "ACEFGHJL": ["H", "G", "J", "C", "A", "F", "L", "E"],
  "ACEFGHJK": ["H", "G", "J", "C", "A", "F", "E", "K"],
  "ACEFGHIL": ["H", "G", "E", "C", "A", "F", "L", "I"],
  "ACEFGHIK": ["H", "G", "E", "C", "A", "F", "I", "K"],
  "ACEFGHIJ": ["H", "G", "J", "C", "A", "F", "E", "I"],
  "ACDGHIJL": ["H", "G", "J", "C", "A", "D", "L", "I"],
  "ACDGHIJK": ["H", "G", "J", "C", "A", "D", "I", "K"],
  "ACDFIJKL": ["C", "J", "I", "D", "A", "F", "L", "K"],
  "ACDFHJKL": ["H", "J", "F", "C", "A", "D", "L", "K"],
  "ACDFHIKL": ["H", "F", "I", "C", "A", "D", "L", "K"],
  "ACDFHIJL": ["H", "J", "F", "C", "A", "D", "L", "I"],
  "ACDFHIJK": ["H", "J", "F", "C", "A", "D", "I", "K"],
  "ACDFGJKL": ["C", "G", "J", "D", "A", "F", "L", "K"],
  "ACDFGIKL": ["C", "G", "I", "D", "A", "F", "L", "K"],
  "ACDFGIJL": ["C", "G", "J", "D", "A", "F", "L", "I"],
  "ACDFGIJK": ["C", "G", "J", "D", "A", "F", "I", "K"],
  "ACDFGHKL": ["H", "G", "F", "C", "A", "D", "L", "K"],
  "ACDFGHJL": ["C", "G", "J", "D", "A", "F", "L", "H"],
  "ACDFGHJK": ["H", "G", "J", "C", "A", "F", "D", "K"],
  "ACDFGHIL": ["H", "G", "F", "C", "A", "D", "L", "I"],
  "ACDFGHIK": ["H", "G", "F", "C", "A", "D", "I", "K"],
  "ACDFGHIJ": ["H", "G", "J", "C", "A", "F", "D", "I"],
  "ACDEIJKL": ["E", "J", "I", "C", "A", "D", "L", "K"],
  "ACDEHJKL": ["H", "J", "E", "C", "A", "D", "L", "K"],
  "ACDEHIKL": ["H", "E", "I", "C", "A", "D", "L", "K"],
  "ACDEHIJL": ["H", "J", "E", "C", "A", "D", "L", "I"],
  "ACDEHIJK": ["H", "J", "E", "C", "A", "D", "I", "K"],
  "ACDEGJKL": ["E", "G", "J", "C", "A", "D", "L", "K"],
  "ACDEGIKL": ["E", "G", "I", "C", "A", "D", "L", "K"],
  "ACDEGIJL": ["E", "G", "J", "C", "A", "D", "L", "I"],
  "ACDEGIJK": ["E", "G", "J", "C", "A", "D", "I", "K"],
  "ACDEGHKL": ["H", "G", "E", "C", "A", "D", "L", "K"],
  "ACDEGHJL": ["H", "G", "J", "C", "A", "D", "L", "E"],
  "ACDEGHJK": ["H", "G", "J", "C", "A", "D", "E", "K"],
  "ACDEGHIL": ["H", "G", "E", "C", "A", "D", "L", "I"],
  "ACDEGHIK": ["H", "G", "E", "C", "A", "D", "I", "K"],
  "ACDEGHIJ": ["H", "G", "J", "C", "A", "D", "E", "I"],
  "ACDEFJKL": ["C", "J", "E", "D", "A", "F", "L", "K"],
  "ACDEFIKL": ["C", "E", "I", "D", "A", "F", "L", "K"],
  "ACDEFIJL": ["C", "J", "E", "D", "A", "F", "L", "I"],
  "ACDEFIJK": ["C", "J", "E", "D", "A", "F", "I", "K"],
  "ACDEFHKL": ["H", "E", "F", "C", "A", "D", "L", "K"],
  "ACDEFHJL": ["H", "J", "F", "C", "A", "D", "L", "E"],
  "ACDEFHJK": ["H", "J", "E", "C", "A", "F", "D", "K"],
  "ACDEFHIL": ["H", "E", "F", "C", "A", "D", "L", "I"],
  "ACDEFHIK": ["H", "E", "F", "C", "A", "D", "I", "K"],
  "ACDEFHIJ": ["H", "J", "E", "C", "A", "F", "D", "I"],
  "ACDEFGKL": ["C", "G", "E", "D", "A", "F", "L", "K"],
  "ACDEFGJL": ["C", "G", "J", "D", "A", "F", "L", "E"],
  "ACDEFGJK": ["C", "G", "J", "D", "A", "F", "E", "K"],
  "ACDEFGIL": ["C", "G", "E", "D", "A", "F", "L", "I"],
  "ACDEFGIK": ["C", "G", "E", "D", "A", "F", "I", "K"],
  "ACDEFGIJ": ["C", "G", "J", "D", "A", "F", "E", "I"],
  "ACDEFGHL": ["H", "G", "F", "C", "A", "D", "L", "E"],
  "ACDEFGHK": ["H", "G", "E", "C", "A", "F", "D", "K"],
  "ACDEFGHJ": ["H", "G", "J", "C", "A", "F", "D", "E"],
  "ACDEFGHI": ["H", "G", "E", "C", "A", "F", "D", "I"],
  "ABGHIJKL": ["H", "J", "B", "A", "I", "G", "L", "K"],
  "ABFHIJKL": ["H", "J", "B", "A", "I", "F", "L", "K"],
  "ABFGIJKL": ["I", "J", "B", "F", "A", "G", "L", "K"],
  "ABFGHJKL": ["H", "J", "B", "F", "A", "G", "L", "K"],
  "ABFGHIKL": ["H", "G", "B", "A", "I", "F", "L", "K"],
  "ABFGHIJL": ["H", "J", "B", "F", "A", "G", "L", "I"],
  "ABFGHIJK": ["H", "J", "B", "F", "A", "G", "I", "K"],
  "ABEHIJKL": ["E", "J", "B", "A", "I", "H", "L", "K"],
  "ABEGIJKL": ["E", "J", "B", "A", "I", "G", "L", "K"],
  "ABEGHJKL": ["E", "J", "B", "A", "H", "G", "L", "K"],
  "ABEGHIKL": ["E", "G", "B", "A", "I", "H", "L", "K"],
  "ABEGHIJL": ["E", "J", "B", "A", "H", "G", "L", "I"],
  "ABEGHIJK": ["E", "J", "B", "A", "H", "G", "I", "K"],
  "ABEFHIJL": ["E", "J", "B", "F", "A", "H", "L", "I"],
  "ABEFHIJK": ["E", "J", "B", "F", "A", "H", "I", "K"],
  "ABEFGJKL": ["E", "J", "B", "F", "A", "G", "L", "K"],
  "ABEFGIKL": ["E", "G", "B", "A", "I", "F", "L", "K"],
  "ABEFGIJL": ["E", "J", "B", "F", "A", "G", "L", "I"],
  "ABEFGIJK": ["E", "J", "B", "F", "A", "G", "I", "K"],
  "ABEFGHKL": ["E", "G", "B", "F", "A", "H", "L", "K"],
  "ABEFGHJL": ["H", "J", "B", "F", "A", "G", "L", "E"],
  "ABEFGHJK": ["H", "J", "B", "F", "A", "G", "E", "K"],
  "ABEFGHIL": ["E", "G", "B", "F", "A", "H", "L", "I"],
  "ABEFGHIK": ["E", "G", "B", "F", "A", "H", "I", "K"],
  "ABEFGHIJ": ["H", "J", "B", "F", "A", "G", "E", "I"],
  "ABDGHIJK": ["H", "J", "B", "D", "A", "G", "I", "K"],
  "ABDFIJKL": ["I", "J", "B", "D", "A", "F", "L", "K"],
  "ABDFHJKL": ["H", "J", "B", "D", "A", "F", "L", "K"],
  "ABDFHIKL": ["H", "I", "B", "D", "A", "F", "L", "K"],
  "ABDFHIJL": ["H", "J", "B", "D", "A", "F", "L", "I"],
  "ABDFHIJK": ["H", "J", "B", "D", "A", "F", "I", "K"],
  "ABDFGJKL": ["F", "J", "B", "D", "A", "G", "L", "K"],
  "ABDFGIKL": ["I", "G", "B", "D", "A", "F", "L", "K"],
  "ABDFGIJL": ["F", "J", "B", "D", "A", "G", "L", "I"],
  "ABDFGIJK": ["F", "J", "B", "D", "A", "G", "I", "K"],
  "ABDFGHKL": ["H", "G", "B", "D", "A", "F", "L", "K"],
  "ABDFGHJL": ["H", "G", "B", "D", "A", "F", "L", "J"],
  "ABDFGHJK": ["H", "G", "B", "D", "A", "F", "J", "K"],
  "ABDFGHIL": ["H", "G", "B", "D", "A", "F", "L", "I"],
  "ABDFGHIK": ["H", "G", "B", "D", "A", "F", "I", "K"],
  "ABDFGHIJ": ["H", "G", "B", "D", "A", "F", "I", "J"],
  "ABDEIJKL": ["E", "J", "B", "A", "I", "D", "L", "K"],
  "ABDEHJKL": ["E", "J", "B", "D", "A", "H", "L", "K"],
  "ABDEHIKL": ["E", "I", "B", "D", "A", "H", "L", "K"],
  "ABDEHIJL": ["E", "J", "B", "D", "A", "H", "L", "I"],
  "ABDEHIJK": ["E", "J", "B", "D", "A", "H", "I", "K"],
  "ABDEGJKL": ["E", "J", "B", "D", "A", "G", "L", "K"],
  "ABDEGIKL": ["E", "G", "B", "A", "I", "D", "L", "K"],
  "ABDEGIJL": ["E", "J", "B", "D", "A", "G", "L", "I"],
  "ABDEGIJK": ["E", "J", "B", "D", "A", "G", "I", "K"],
  "ABDEGHKL": ["E", "G", "B", "D", "A", "H", "L", "K"],
  "ABDEGHJL": ["H", "J", "B", "D", "A", "G", "L", "E"],
  "ABDEGHJK": ["H", "J", "B", "D", "A", "G", "E", "K"],
  "ABDEGHIL": ["E", "G", "B", "D", "A", "H", "L", "I"],
  "ABDEGHIK": ["E", "G", "B", "D", "A", "H", "I", "K"],
  "ABDEGHIJ": ["H", "J", "B", "D", "A", "G", "E", "I"],
  "ABDEFJKL": ["E", "J", "B", "D", "A", "F", "L", "K"],
  "ABDEFIKL": ["E", "I", "B", "D", "A", "F", "L", "K"],
  "ABDEFIJL": ["E", "J", "B", "D", "A", "F", "L", "I"],
  "ABDEFIJK": ["E", "J", "B", "D", "A", "F", "I", "K"],
  "ABDEFHKL": ["H", "E", "B", "D", "A", "F", "L", "K"],
  "ABDEFHJL": ["H", "J", "B", "D", "A", "F", "L", "E"],
  "ABDEFHJK": ["H", "J", "B", "D", "A", "F", "E", "K"],
  "ABDEFHIL": ["H", "E", "B", "D", "A", "F", "L", "I"],
  "ABDEFHIK": ["H", "E", "B", "D", "A", "F", "I", "K"],
  "ABDEFHIJ": ["H", "J", "B", "D", "A", "F", "E", "I"],
  "ABDEFGKL": ["E", "G", "B", "D", "A", "F", "L", "K"],
  "ABDEFGJL": ["E", "G", "B", "D", "A", "F", "L", "J"],
  "ABDEFGJK": ["E", "G", "B", "D", "A", "F", "J", "K"],
  "ABDEFGIL": ["E", "G", "B", "D", "A", "F", "L", "I"],
  "ABDEFGIK": ["E", "G", "B", "D", "A", "F", "I", "K"],
  "ABDEFGIJ": ["E", "G", "B", "D", "A", "F", "I", "J"],
  "ABDEFGHL": ["H", "G", "B", "D", "A", "F", "L", "E"],
  "ABDEFGHK": ["H", "G", "B", "D", "A", "F", "E", "K"],
  "ABDEFGHJ": ["H", "G", "B", "D", "A", "F", "E", "J"],
  "ABDEFGHI": ["H", "G", "B", "D", "A", "F", "E", "I"],
  "ABCGHIJK": ["H", "J", "B", "C", "A", "G", "I", "K"],
  "ABCFIJKL": ["I", "J", "B", "C", "A", "F", "L", "K"],
  "ABCFHJKL": ["H", "J", "B", "C", "A", "F", "L", "K"],
  "ABCFHIKL": ["H", "I", "B", "C", "A", "F", "L", "K"],
  "ABCFHIJL": ["H", "J", "B", "C", "A", "F", "L", "I"],
  "ABCFHIJK": ["H", "J", "B", "C", "A", "F", "I", "K"],
  "ABCFGJKL": ["C", "J", "B", "F", "A", "G", "L", "K"],
  "ABCFGIKL": ["I", "G", "B", "C", "A", "F", "L", "K"],
  "ABCFGIJL": ["C", "J", "B", "F", "A", "G", "L", "I"],
  "ABCFGIJK": ["C", "J", "B", "F", "A", "G", "I", "K"],
  "ABCFGHKL": ["H", "G", "B", "C", "A", "F", "L", "K"],
  "ABCFGHJL": ["H", "G", "B", "C", "A", "F", "L", "J"],
  "ABCFGHJK": ["H", "G", "B", "C", "A", "F", "J", "K"],
  "ABCFGHIL": ["H", "G", "B", "C", "A", "F", "L", "I"],
  "ABCFGHIK": ["H", "G", "B", "C", "A", "F", "I", "K"],
  "ABCFGHIJ": ["H", "G", "B", "C", "A", "F", "I", "J"],
  "ABCEIJKL": ["E", "J", "B", "A", "I", "C", "L", "K"],
  "ABCEHJKL": ["E", "J", "B", "C", "A", "H", "L", "K"],
  "ABCEHIKL": ["E", "I", "B", "C", "A", "H", "L", "K"],
  "ABCEHIJL": ["E", "J", "B", "C", "A", "H", "L", "I"],
  "ABCEHIJK": ["E", "J", "B", "C", "A", "H", "I", "K"],
  "ABCEGJKL": ["E", "J", "B", "C", "A", "G", "L", "K"],
  "ABCEGIKL": ["E", "G", "B", "A", "I", "C", "L", "K"],
  "ABCEGIJL": ["E", "J", "B", "C", "A", "G", "L", "I"],
  "ABCEGIJK": ["E", "J", "B", "C", "A", "G", "I", "K"],
  "ABCEGHKL": ["E", "G", "B", "C", "A", "H", "L", "K"],
  "ABCEGHJL": ["H", "J", "B", "C", "A", "G", "L", "E"],
  "ABCEGHJK": ["H", "J", "B", "C", "A", "G", "E", "K"],
  "ABCEGHIL": ["E", "G", "B", "C", "A", "H", "L", "I"],
  "ABCEGHIK": ["E", "G", "B", "C", "A", "H", "I", "K"],
  "ABCEGHIJ": ["H", "J", "B", "C", "A", "G", "E", "I"],
  "ABCEFJKL": ["E", "J", "B", "C", "A", "F", "L", "K"],
  "ABCEFIKL": ["E", "I", "B", "C", "A", "F", "L", "K"],
  "ABCEFIJL": ["E", "J", "B", "C", "A", "F", "L", "I"],
  "ABCEFIJK": ["E", "J", "B", "C", "A", "F", "I", "K"],
  "ABCEFHKL": ["H", "E", "B", "C", "A", "F", "L", "K"],
  "ABCEFHJL": ["H", "J", "B", "C", "A", "F", "L", "E"],
  "ABCEFHJK": ["H", "J", "B", "C", "A", "F", "E", "K"],
  "ABCEFHIL": ["H", "E", "B", "C", "A", "F", "L", "I"],
  "ABCEFHIK": ["H", "E", "B", "C", "A", "F", "I", "K"],
  "ABCEFHIJ": ["H", "J", "B", "C", "A", "F", "E", "I"],
  "ABCEFGKL": ["E", "G", "B", "C", "A", "F", "L", "K"],
  "ABCEFGJL": ["E", "G", "B", "C", "A", "F", "L", "J"],
  "ABCEFGJK": ["E", "G", "B", "C", "A", "F", "J", "K"],
  "ABCEFGIL": ["E", "G", "B", "C", "A", "F", "L", "I"],
  "ABCEFGIK": ["E", "G", "B", "C", "A", "F", "I", "K"],
  "ABCEFGIJ": ["E", "G", "B", "C", "A", "F", "I", "J"],
  "ABCEFGHL": ["H", "G", "B", "C", "A", "F", "L", "E"],
  "ABCEFGHK": ["H", "G", "B", "C", "A", "F", "E", "K"],
  "ABCEFGHJ": ["H", "G", "B", "C", "A", "F", "E", "J"],
  "ABCEFGHI": ["H", "G", "B", "C", "A", "F", "E", "I"],
  "ABCDHIJK": ["H", "J", "B", "C", "A", "D", "I", "K"],
  "ABCDFJKL": ["C", "J", "B", "D", "A", "G", "L", "K"],
  "ABCDFIKL": ["I", "G", "B", "C", "A", "D", "L", "K"],
  "ABCDFIJL": ["C", "J", "B", "D", "A", "G", "L", "I"],
  "ABCDFIJK": ["C", "J", "B", "D", "A", "G", "I", "K"],
  "ABCDFHKL": ["H", "G", "B", "C", "A", "D", "L", "K"],
  "ABCDFHJL": ["H", "G", "B", "C", "A", "D", "L", "J"],
  "ABCDFHJK": ["H", "G", "B", "C", "A", "D", "J", "K"],
  "ABCDFHIL": ["H", "G", "B", "C", "A", "D", "L", "I"],
  "ABCDFHIK": ["H", "G", "B", "C", "A", "D", "I", "K"],
  "ABCDFHIJ": ["H", "G", "B", "C", "A", "D", "I", "J"],
  "ABCDEFJL": ["C", "J", "B", "D", "A", "F", "L", "E"],
  "ABCDEFJK": ["C", "J", "B", "D", "A", "F", "E", "K"],
  "ABCDEFIL": ["C", "E", "B", "D", "A", "F", "L", "I"],
  "ABCDEFIK": ["C", "E", "B", "D", "A", "F", "I", "K"],
  "ABCDEFIJ": ["C", "J", "B", "D", "A", "F", "E", "I"],
  "ABCDEFHL": ["H", "F", "B", "C", "A", "D", "L", "E"],
  "ABCDEFHK": ["H", "E", "B", "C", "A", "F", "D", "K"],
  "ABCDEFHJ": ["H", "J", "B", "C", "A", "F", "D", "E"],
  "ABCDEFHI": ["H", "E", "B", "C", "A", "F", "D", "I"],
  "ABCDEFGL": ["C", "G", "B", "D", "A", "F", "L", "E"],
  "ABCDEFGK": ["C", "G", "B", "D", "A", "F", "E", "K"],
  "ABCDEFGJ": ["C", "G", "B", "D", "A", "F", "E", "J"],
  "ABCDEFGI": ["C", "G", "B", "D", "A", "F", "E", "I"],
  "ABCDEFGH": ["H", "G", "B", "C", "A", "F", "D", "E"],
};

const THIRD_PLACE_RULE_FALLBACKS = {
  "ABCDEFKL": ["C", "E", "B", "D", "A", "F", "L", "K"],
  "ABCDEGHI": ["H", "G", "B", "C", "A", "D", "E", "I"],
  "ABCDEGHJ": ["H", "G", "B", "C", "A", "D", "E", "J"],
  "ABCDEGHK": ["H", "G", "B", "C", "A", "D", "E", "K"],
  "ABCDEGHL": ["H", "G", "B", "C", "A", "D", "L", "E"],
  "ABCDEGIJ": ["E", "G", "B", "C", "A", "D", "I", "J"],
  "ABCDEGIK": ["E", "G", "B", "C", "A", "D", "I", "K"],
  "ABCDEGIL": ["E", "G", "B", "C", "A", "D", "L", "I"],
  "ABCDEGJK": ["E", "G", "B", "C", "A", "D", "J", "K"],
  "ABCDEGJL": ["E", "G", "B", "C", "A", "D", "L", "J"],
  "ABCDEGKL": ["E", "G", "B", "C", "A", "D", "L", "K"],
  "ABCDEHIJ": ["H", "J", "B", "C", "A", "D", "E", "I"],
  "ABCDEHIK": ["H", "E", "B", "C", "A", "D", "I", "K"],
  "ABCDEHIL": ["H", "E", "B", "C", "A", "D", "L", "I"],
  "ABCDEHJK": ["H", "J", "B", "C", "A", "D", "E", "K"],
  "ABCDEHJL": ["H", "J", "B", "C", "A", "D", "L", "E"],
  "ABCDEHKL": ["H", "E", "B", "C", "A", "D", "L", "K"],
  "ABCDEIJK": ["E", "J", "B", "C", "A", "D", "I", "K"],
  "ABCDEIJL": ["E", "J", "B", "C", "A", "D", "L", "I"],
  "ABCDEIKL": ["E", "I", "B", "C", "A", "D", "L", "K"],
  "ABCDEJKL": ["E", "J", "B", "C", "A", "D", "L", "K"],
  "ABCDFGHI": ["H", "G", "B", "C", "A", "F", "D", "I"],
  "ABCDFGHJ": ["H", "G", "B", "C", "A", "F", "D", "J"],
  "ABCDFGHK": ["H", "G", "B", "C", "A", "F", "D", "K"],
  "ABCDFGHL": ["C", "G", "B", "D", "A", "F", "L", "H"],
  "ABCDFGIJ": ["C", "G", "B", "D", "A", "F", "I", "J"],
  "ABCDFGIK": ["C", "G", "B", "D", "A", "F", "I", "K"],
  "ABCDFGIL": ["C", "G", "B", "D", "A", "F", "L", "I"],
  "ABCDFGJK": ["C", "G", "B", "D", "A", "F", "J", "K"],
  "ABCDFGJL": ["C", "G", "B", "D", "A", "F", "L", "J"],
  "ABCDFGKL": ["C", "G", "B", "D", "A", "F", "L", "K"],
  "ABCDGHIJ": ["H", "G", "B", "C", "A", "D", "I", "J"],
  "ABCDGHIK": ["H", "G", "B", "C", "A", "D", "I", "K"],
  "ABCDGHIL": ["H", "G", "B", "C", "A", "D", "L", "I"],
  "ABCDGHJK": ["H", "G", "B", "C", "A", "D", "J", "K"],
  "ABCDGHJL": ["H", "G", "B", "C", "A", "D", "L", "J"],
  "ABCDGHKL": ["H", "G", "B", "C", "A", "D", "L", "K"],
  "ABCDGIJK": ["C", "J", "B", "D", "A", "G", "I", "K"],
  "ABCDGIJL": ["C", "J", "B", "D", "A", "G", "L", "I"],
  "ABCDGIKL": ["I", "G", "B", "C", "A", "D", "L", "K"],
  "ABCDGJKL": ["C", "J", "B", "D", "A", "G", "L", "K"],
  "ABCDHIJL": ["H", "J", "B", "C", "A", "D", "L", "I"],
  "ABCDHIKL": ["H", "I", "B", "C", "A", "D", "L", "K"],
  "ABCDHJKL": ["H", "J", "B", "C", "A", "D", "L", "K"],
  "ABCDIJKL": ["I", "J", "B", "C", "A", "D", "L", "K"],
  "ABCGHIJL": ["H", "J", "B", "C", "A", "G", "L", "I"],
  "ABCGHIKL": ["I", "G", "B", "C", "A", "H", "L", "K"],
  "ABCGHJKL": ["H", "J", "B", "C", "A", "G", "L", "K"],
  "ABCGIJKL": ["I", "J", "B", "C", "A", "G", "L", "K"],
  "ABCHIJKL": ["I", "J", "B", "C", "A", "H", "L", "K"],
  "ABDGHIJL": ["H", "J", "B", "D", "A", "G", "L", "I"],
  "ABDGHIKL": ["I", "G", "B", "D", "A", "H", "L", "K"],
  "ABDGHJKL": ["H", "J", "B", "D", "A", "G", "L", "K"],
  "ABDGIJKL": ["I", "J", "B", "D", "A", "G", "L", "K"],
  "ABDHIJKL": ["I", "J", "B", "D", "A", "H", "L", "K"],
  "ABEFHIKL": ["E", "I", "B", "F", "A", "H", "L", "K"],
  "ABEFHJKL": ["E", "J", "B", "F", "A", "H", "L", "K"],
  "ABEFIJKL": ["E", "J", "B", "A", "I", "F", "L", "K"],
  "ACDGHIKL": ["H", "G", "I", "C", "A", "D", "L", "K"],
  "ACDGHJKL": ["H", "G", "J", "C", "A", "D", "L", "K"],
  "ACDGIJKL": ["I", "G", "J", "C", "A", "D", "L", "K"],
  "ACDHIJKL": ["H", "J", "I", "C", "A", "D", "L", "K"],
  "ACEFHIKL": ["H", "E", "I", "C", "A", "F", "L", "K"],
  "ACEFHJKL": ["H", "J", "E", "C", "A", "F", "L", "K"],
  "ACEFIJKL": ["E", "J", "I", "C", "A", "F", "L", "K"],
  "ADEFHIKL": ["H", "E", "I", "D", "A", "F", "L", "K"],
  "ADEFHJKL": ["H", "J", "E", "D", "A", "F", "L", "K"],
  "ADEFIJKL": ["E", "J", "I", "D", "A", "F", "L", "K"],
  "BCDGHIKL": ["H", "G", "B", "C", "I", "D", "L", "K"],
  "BCDGHJKL": ["H", "G", "B", "C", "J", "D", "L", "K"],
  "BCDGIJKL": ["I", "G", "B", "C", "J", "D", "L", "K"],
  "BCDHIJKL": ["H", "J", "B", "C", "I", "D", "L", "K"],
  "BCEFHIKL": ["E", "I", "B", "C", "H", "F", "L", "K"],
  "BCEFHJKL": ["E", "J", "B", "C", "H", "F", "L", "K"],
  "BCEFIJKL": ["E", "J", "B", "C", "I", "F", "L", "K"],
  "BDEFHIKL": ["E", "I", "B", "D", "H", "F", "L", "K"],
  "BDEFHJKL": ["E", "J", "B", "D", "H", "F", "L", "K"],
  "BDEFIJKL": ["E", "J", "B", "D", "I", "F", "L", "K"],
};

const WcStandingsCalculator = () => {
  const { matches, teams, name, loading, error } = useTeamCompetitionData(COMPETITION_ID);
  
  const [localMatches, setLocalMatches] = useState({});
  const [bracketScores, setBracketScores] = useState({});
  const [selectedGroup, setSelectedGroup] = useState('A');
  const [hoveredMatchTeams, setHoveredMatchTeams] = useState(null);
  const [hoveredTableTeamId, setHoveredTableTeamId] = useState(null);

  const leagueNameDisplay = name || 'Mistrzostwa Świata 2026';

  const actualKnockoutResults = useMemo(() => {
    const results = {};
    (matches || []).forEach(m => {
      const stage = getWorldCupKnockoutStage(m);
      const status = (m.status || '').toString().toLowerCase();
      const hasTeams = m.homeTeamId && m.awayTeamId;
      const hasScore = m.homeGoals !== null && m.homeGoals !== undefined && m.awayGoals !== null && m.awayGoals !== undefined;

      if (!stage || !hasTeams || !hasScore || (status !== 'ended' && status !== 'finished')) return;

      results[`${stage}:${teamPairKey(m.homeTeamId, m.awayTeamId)}`] = {
        homeTeamId: Number(m.homeTeamId),
        awayTeamId: Number(m.awayTeamId),
        homeGoals: Number(m.homeGoals),
        awayGoals: Number(m.awayGoals),
        penaltyWinner: getPenaltyWinnerId(m)
      };
    });
    return results;
  }, [matches]);

  // Inicjalizacja lokalnych meczów na podstawie API
  const initializeMatches = () => {
    if (matches?.length > 0) {
      const initialMatchesState = {};
      matches.forEach(m => {
        const isPlanned = m.status && m.status.toLowerCase() === 'planned';
        const groupLetter = getWorldCupGroupFromMatch(m);
        if (!groupLetter) return;

        initialMatchesState[m.matchId || m.id] = {
          matchId: m.matchId || m.id,
          homeGoals: isPlanned || m.homeGoals === null ? '' : Number(m.homeGoals),
          awayGoals: isPlanned || m.awayGoals === null ? '' : Number(m.awayGoals),
          homeTeamId: m.homeTeamId,
          awayTeamId: m.awayTeamId,
          homeTeamName: m.homeTeamName || m.homeTeam?.name,
          awayTeamName: m.awayTeamName || m.awayTeam?.name,
          group: groupLetter
        };
      });
      setLocalMatches(initialMatchesState);
      setBracketScores({});
    }
  };

  useEffect(() => {
    initializeMatches();
  }, [matches]);

  const handleGoalChange = (matchId, side, operation) => {
    setLocalMatches(prev => {
      const match = prev[matchId];
      let home = match.homeGoals;
      let away = match.awayGoals;

      if (home === '' || away === '') {
        if (operation === 'increment') {
          home = side === 'homeGoals' ? 1 : 0;
          away = side === 'awayGoals' ? 1 : 0;
        } else if (operation === 'decrement') {
          home = 0; away = 0;
        }
      } else {
        if (operation === 'increment') {
          if (side === 'homeGoals') home += 1;
          else away += 1;
        } else {
          if (side === 'homeGoals') home = Math.max(0, home - 1);
          else away = Math.max(0, away - 1);
        }
      }
      return { ...prev, [matchId]: { ...match, homeGoals: home, awayGoals: away } };
    });
  };

  const clearMatch = (matchId) => {
    setLocalMatches(prev => ({ ...prev, [matchId]: { ...prev[matchId], homeGoals: '', awayGoals: '' } }));
  };

  const randomizeMatches = (scope) => {
    setLocalMatches(prev => {
      const nextState = { ...prev };
      Object.values(nextState).forEach(m => {
        if (m.homeGoals === '' && m.awayGoals === '') {
          if (scope === 'season' || (scope === 'group' && m.group === selectedGroup)) {
            nextState[m.matchId] = {
              ...m,
              homeGoals: Math.floor(Math.random() * 4),
              awayGoals: Math.floor(Math.random() * 4)
            };
          }
        }
      });
      return nextState;
    });
  };

  // Kalkulator tabel grupowych z tie-breakerami H2H
  const groupTables = useMemo(() => {
    const tables = {};
    GROUPS.forEach(g => {
      const gMatches = Object.values(localMatches).filter(m => m.group === g);
      const stats = {};
      
      // Inicjalizacja
      gMatches.forEach(m => {
        if (!stats[m.homeTeamId]) stats[m.homeTeamId] = { teamId: m.homeTeamId, teamName: m.homeTeamName, points: 0, goalsScored: 0, goalsConceded: 0, matchesPlayed: 0 };
        if (!stats[m.awayTeamId]) stats[m.awayTeamId] = { teamId: m.awayTeamId, teamName: m.awayTeamName, points: 0, goalsScored: 0, goalsConceded: 0, matchesPlayed: 0 };
      });

      // Podliczanie bazy
      gMatches.forEach(m => {
        if (m.homeGoals !== '' && m.awayGoals !== '') {
          const hG = Number(m.homeGoals), aG = Number(m.awayGoals);
          stats[m.homeTeamId].matchesPlayed += 1; stats[m.awayTeamId].matchesPlayed += 1;
          stats[m.homeTeamId].goalsScored += hG; stats[m.homeTeamId].goalsConceded += aG;
          stats[m.awayTeamId].goalsScored += aG; stats[m.awayTeamId].goalsConceded += hG;

          if (hG > aG) stats[m.homeTeamId].points += 3;
          else if (hG < aG) stats[m.awayTeamId].points += 3;
          else { stats[m.homeTeamId].points += 1; stats[m.awayTeamId].points += 1; }
        }
      });

      const teamsArray = Object.values(stats);
      teamsArray.sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        const tiedTeams = teamsArray.filter(t => t.points === a.points);
        
        if (tiedTeams.length >= 2) {
          const tiedIds = tiedTeams.map(t => t.teamId);
          const miniStats = {};
          tiedIds.forEach(id => miniStats[id] = { points: 0, goalsScored: 0, goalsConceded: 0 });

          gMatches.forEach(m => {
            if (m.homeGoals !== '' && m.awayGoals !== '' && tiedIds.includes(m.homeTeamId) && tiedIds.includes(m.awayTeamId)) {
              const hG = Number(m.homeGoals), aG = Number(m.awayGoals);
              miniStats[m.homeTeamId].goalsScored += hG; miniStats[m.homeTeamId].goalsConceded += aG;
              miniStats[m.awayTeamId].goalsScored += aG; miniStats[m.awayTeamId].goalsConceded += hG;
              if (hG > aG) miniStats[m.homeTeamId].points += 3;
              else if (hG < aG) miniStats[m.awayTeamId].points += 3;
              else { miniStats[m.homeTeamId].points += 1; miniStats[m.awayTeamId].points += 1; }
            }
          });

          if (miniStats[b.teamId].points !== miniStats[a.teamId].points) return miniStats[b.teamId].points - miniStats[a.teamId].points;
          const diffA_mini = miniStats[a.teamId].goalsScored - miniStats[a.teamId].goalsConceded;
          const diffB_mini = miniStats[b.teamId].goalsScored - miniStats[b.teamId].goalsConceded;
          if (diffB_mini !== diffA_mini) return diffB_mini - diffA_mini;
          if (miniStats[b.teamId].goalsScored !== miniStats[a.teamId].goalsScored) return miniStats[b.teamId].goalsScored - miniStats[a.teamId].goalsScored;
        }

        const diffA_global = a.goalsScored - a.goalsConceded;
        const diffB_global = b.goalsScored - b.goalsConceded;
        if (diffB_global !== diffA_global) return diffB_global - diffA_global;
        return b.goalsScored - a.goalsScored;
      });

      tables[g] = teamsArray;
    });
    return tables;
  }, [localMatches]);

  // Ranking 3. Miejsc
  const thirdPlaceTable = useMemo(() => {
    const thirds = [];
    GROUPS.forEach(g => {
      const table = groupTables[g] || [];
      if (table.length >= 3) thirds.push({ ...table[2], group: g });
    });

    return thirds.sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      const gdA = a.goalsScored - a.goalsConceded;
      const gdB = b.goalsScored - b.goalsConceded;
      if (gdB !== gdA) return gdB - gdA;
      return b.goalsScored - a.goalsScored;
    });
  }, [groupTables]);
  
  const handleBracketScoreChange = (matchKey, field, val) => {
  setBracketScores(prev => {
    const match = prev[matchKey] || { homeScore: '', awayScore: '', penaltyWinner: null };
    const updated = { ...match, [field]: val };
    
    // Jeśli po zmianie goli nie ma remisu, zresetuj zwycięzcę karnych
    if (field === 'homeScore' || field === 'awayScore') {
      const h = field === 'homeScore' ? val : updated.homeScore;
      const a = field === 'awayScore' ? val : updated.awayScore;
      if (h !== '' && a !== '' && parseInt(h, 10) !== parseInt(a, 10)) {
        updated.penaltyWinner = null;
      }
    }
    return { ...prev, [matchKey]: updated };
  });
};

const handleBracketGoal = (matchKey, side, operation) => {
  setBracketScores(prev => {
    const match = prev[matchKey] || { homeGoals: '', awayGoals: '', penaltyWinner: null };
    let home = match.homeGoals;
    let away = match.awayGoals;

    if (home === '' || away === '') {
      if (operation === 'increment') {
        home = side === 'homeGoals' ? 1 : 0;
        away = side === 'awayGoals' ? 1 : 0;
      } else if (operation === 'decrement') {
        home = 0; away = 0;
      }
    } else {
      if (operation === 'increment') {
        if (side === 'homeGoals') home += 1; else away += 1;
      } else {
        if (side === 'homeGoals') home = Math.max(0, home - 1); else away = Math.max(0, away - 1);
      }
    }

    const updated = { ...match, homeGoals: home, awayGoals: away, source: 'manual' };
    
    // Jeśli nie ma remisu, usuń zwycięzcę karnych
    if (home !== '' && away !== '' && home !== away) {
      updated.penaltyWinner = null;
    }
    
    return { ...prev, [matchKey]: updated };
  });
};

const getMatchWinner = (homeTeam, awayTeam, matchKey) => {
  if (!homeTeam && !awayTeam) return null;
  const score = bracketScores[matchKey];
  if (!score || score.homeScore === '' || score.awayScore === '') return null;

  const h = parseInt(score.homeScore, 10);
  const a = parseInt(score.awayScore, 10);

  if (h > a) return homeTeam;
  if (a > h) return awayTeam;
  
  // W przypadku remisu decyduje wskazany zwycięzca karnych
  if (h === a) {
    if (score.penaltyWinner === 'home') return homeTeam;
    if (score.penaltyWinner === 'away') return awayTeam;
  }
  return null;
};

const setPenaltyWinner = (matchKey, teamSide) => {
  setBracketScores(prev => ({
    ...prev,
    [matchKey]: { ...(prev[matchKey] || {}), penaltyWinner: teamSide, source: 'manual' }
  }));
};

const clearBracketMatch = (matchKey) => {
  setBracketScores(prev => ({
    ...prev,
    [matchKey]: { homeGoals: '', awayGoals: '', penaltyWinner: null }
  }));
};

const getWinnerAndLoser = (homeTeam, awayTeam, matchKey) => {
  if (!homeTeam && !awayTeam) return { winner: null, loser: null };
  const score = bracketScores[matchKey];
  if (!score || score.homeGoals === '' || score.awayGoals === '') return { winner: null, loser: null };

  const h = parseInt(score.homeGoals, 10);
  const a = parseInt(score.awayGoals, 10);

  if (h > a) return { winner: homeTeam, loser: awayTeam };
  if (a > h) return { winner: awayTeam, loser: homeTeam };
  
  if (h === a) {
    if (score.penaltyWinner === 'home') return { winner: homeTeam, loser: awayTeam };
    if (score.penaltyWinner === 'away') return { winner: awayTeam, loser: homeTeam };
  }
  return { winner: null, loser: null };
};

const getActualBracketScore = useCallback((stage, match) => {
  if (!match?.home?.teamId || !match?.away?.teamId) return null;
  const actual = actualKnockoutResults[`${stage}:${teamPairKey(match.home.teamId, match.away.teamId)}`];
  if (!actual) return null;

  const sameHomeOrder = actual.homeTeamId === Number(match.home.teamId);
  const homeGoals = sameHomeOrder ? actual.homeGoals : actual.awayGoals;
  const awayGoals = sameHomeOrder ? actual.awayGoals : actual.homeGoals;

  let penaltyWinner = null;
  if (actual.penaltyWinner) {
    if (Number(actual.penaltyWinner) === Number(match.home.teamId)) penaltyWinner = 'home';
    if (Number(actual.penaltyWinner) === Number(match.away.teamId)) penaltyWinner = 'away';
  }

  return { homeGoals, awayGoals, penaltyWinner };
}, [actualKnockoutResults]);



 const roundOf32Matches = useMemo(() => {
    const advancingThirds = thirdPlaceTable.slice(0, 8);
    const thirdPlaceKey = advancingThirds.map(t => t.group).sort().join('');
    const ruleMapping = THIRD_PLACE_RULES[thirdPlaceKey] || THIRD_PLACE_RULE_FALLBACKS[thirdPlaceKey] || Array(8).fill("?");
    
    const getTeam = (g, pos) => groupTables[g]?.[pos - 1] || { teamName: '---' };
    const getThird = (idx) => advancingThirds.find(t => t.group === ruleMapping[idx]) || { teamName: `3. msc [${ruleMapping[idx] || '?'}]` };

    return [
      // (1) M73 Runner‑up A (2A) v. Runner‑up B (2B)
      { id: 1, home: getTeam('A', 2), away: getTeam('B', 2) },
      // (2) M74 Winner E (1E) v. Best 3rd place
      { id: 2, home: getTeam('E', 1), away: getThird(3) }, 
      // (3) M75 Winner F (1F) v. Runner‑up C (2C)
      { id: 3, home: getTeam('F', 1), away: getTeam('C', 2) },
      // (4) M76 Winner C (1C) v. Runner‑up F (2F)
      { id: 4, home: getTeam('C', 1), away: getTeam('F', 2) },
      // (5) M77 Winner I (1I) v. Best 3rd place
      { id: 5, home: getTeam('I', 1), away: getThird(5) },
      // (6) M78 Runner‑up E (2E) v. Runner‑up I (2I)
      { id: 6, home: getTeam('E', 2), away: getTeam('I', 2) },
      // (7) M79 Winner A (1A) v. Best 3rd place
      { id: 7, home: getTeam('A', 1), away: getThird(0) },
      // (8) M80 Winner L (1L) v. Best 3rd place
      { id: 8, home: getTeam('L', 1), away: getThird(7) },
      // (9) M81 Winner D (1D) v. Best 3rd place
      { id: 9, home: getTeam('D', 1), away: getThird(2) },
      // (10) M82 Winner G (1G) v. Best 3rd place
      { id: 10, home: getTeam('G', 1), away: getThird(4) },
      // (11) M83 Runner‑up K (2K) v. Runner‑up L (2L)
      { id: 11, home: getTeam('K', 2), away: getTeam('L', 2) },
      // (12) M84 Winner H (1H) v. Runner‑up J (2J)
      { id: 12, home: getTeam('H', 1), away: getTeam('J', 2) },
      // (13) M85 Winner B (1B) v. Best 3rd place
      { id: 13, home: getTeam('B', 1), away: getThird(1) },
      // (14) M86 Winner J (1J) v. Runner‑up H (2H)
      { id: 14, home: getTeam('J', 1), away: getTeam('H', 2) },
      // (15) M87 Winner K (1K) v. Best 3rd place
      { id: 15, home: getTeam('K', 1), away: getThird(6) },
      // (16) M88 Runner‑up D (2D) v. Runner‑up G (2G)
      { id: 16, home: getTeam('D', 2), away: getTeam('G', 2) }
    ];
  }, [groupTables, thirdPlaceTable]);
  
  // Pomocnicza funkcja pobierająca z 1/16 (indeksy w tablicy są od 0, więc M73 (id=1) to index 0)
  const getR32 = (matchId) => roundOf32Matches[matchId - 1];

  // Drabinka 1/8 (Matches 17-24)
  const roundOf16 = useMemo(() => {
    // Tablica mapująca kto z kim gra: [home_id, away_id] - id od 1 do 16
    const pairs = [
      [2, 5],   // (17) M89: W74 (nr 2) vs W77 (nr 5)
      [1, 3],   // (18) M90: W73 (nr 1) vs W75 (nr 3)
      [4, 6],   // (19) M91: W76 (nr 4) vs W78 (nr 6)
      [7, 8],   // (20) M92: W79 (nr 7) vs W80 (nr 8)
      [11, 12], // (21) M93: W83 (nr 11) vs W84 (nr 12)
      [9, 10],  // (22) M94: W81 (nr 9) vs W82 (nr 10)
      [14, 16], // (23) M95: W86 (nr 14) vs W88 (nr 16)
      [13, 15]  // (24) M96: W85 (nr 13) vs W87 (nr 15)
    ];

    return pairs.map(([homeId, awayId], i) => {
      const m1 = getR32(homeId);
      const m2 = getR32(awayId);
      // klucz dla r32 pochodzi od indeksu, więc domyślnie jest to id - 1
      return {
        matchKey: `r16_${i}`,
        id: 17 + i,
        home: getWinnerAndLoser(m1?.home, m1?.away, `r32_${homeId - 1}`).winner,
        away: getWinnerAndLoser(m2?.home, m2?.away, `r32_${awayId - 1}`).winner,
        placeholderHome: `Zw. M${homeId + 72}`, // +72 by pokazywać numery np. M73
        placeholderAway: `Zw. M${awayId + 72}`
      };
    });
  }, [roundOf32Matches, bracketScores]);

  // Pomocnicza funkcja pobierająca z 1/8 (indeksy w tablicy są od 0, więc M89 (id=17) to index 0)
  const getR16 = (matchId) => roundOf16[matchId - 17];

  // Drabinka 1/4 (Matches A, B, C, D)
  const quarterfinals = useMemo(() => {
    // Tablica mapująca: [home_id, away_id] - id od 17 do 24
    const pairs = [
      [17, 18], // (A) M97: W89 (nr 17) vs W90 (nr 18)
      [21, 22], // (B) M98: W93 (nr 21) vs W94 (nr 22)
      [19, 20], // (C) M99: W91 (nr 19) vs W92 (nr 20)
      [23, 24]  // (D) M100: W95 (nr 23) vs W96 (nr 24)
    ];

    return pairs.map(([homeId, awayId], i) => {
      const m1 = getR16(homeId);
      const m2 = getR16(awayId);
      return {
        matchKey: `qf_${i}`,
        id: i, // Tylko wew. id dla liter A-D
        home: getWinnerAndLoser(m1?.home, m1?.away, `r16_${homeId - 17}`).winner,
        away: getWinnerAndLoser(m2?.home, m2?.away, `r16_${awayId - 17}`).winner,
        placeholderHome: `Zw. M${homeId + 72}`,
        placeholderAway: `Zw. M${awayId + 72}`
      };
    });
  }, [roundOf16, bracketScores]);

  // Drabinka 1/2 (Matches SF1, SF2)
  const semifinals = useMemo(() => {
    return [
      {
        matchKey: `sf_0`,
        home: getWinnerAndLoser(quarterfinals[0]?.home, quarterfinals[0]?.away, `qf_0`).winner, // M97
        away: getWinnerAndLoser(quarterfinals[1]?.home, quarterfinals[1]?.away, `qf_1`).winner, // M98
        placeholderHome: `Zw. Ćwierć. (M97)`,
        placeholderAway: `Zw. Ćwierć. (M98)`
      },
      {
        matchKey: `sf_1`,
        home: getWinnerAndLoser(quarterfinals[2]?.home, quarterfinals[2]?.away, `qf_2`).winner, // M99
        away: getWinnerAndLoser(quarterfinals[3]?.home, quarterfinals[3]?.away, `qf_3`).winner, // M100
        placeholderHome: `Zw. Ćwierć. (M99)`,
        placeholderAway: `Zw. Ćwierć. (M100)`
      }
    ];
  }, [quarterfinals, bracketScores]);

  const finalMatch = useMemo(() => {
    return {
      matchKey: `final_0`,
      home: getWinnerAndLoser(semifinals[0]?.home, semifinals[0]?.away, `sf_0`).winner,
      away: getWinnerAndLoser(semifinals[1]?.home, semifinals[1]?.away, `sf_1`).winner,
      placeholderHome: `Zwycięzca Pół. 1`,
      placeholderAway: `Zwycięzca Pół. 2`
    };
  }, [semifinals, bracketScores]);

  const thirdPlaceMatch = useMemo(() => {
    return {
      matchKey: `third_0`,
      home: getWinnerAndLoser(semifinals[0]?.home, semifinals[0]?.away, `sf_0`).loser,
      away: getWinnerAndLoser(semifinals[1]?.home, semifinals[1]?.away, `sf_1`).loser,
      placeholderHome: `Przegrany Pół. 1`,
      placeholderAway: `Przegrany Pół. 2`
    };
  }, [semifinals, bracketScores]);

  useEffect(() => {
    const candidates = [
      ...roundOf32Matches.map((match, i) => ({ stage: 'r32', matchKey: `r32_${i}`, match })),
      ...roundOf16.map(match => ({ stage: 'r16', matchKey: match.matchKey, match })),
      ...quarterfinals.map(match => ({ stage: 'qf', matchKey: match.matchKey, match })),
      ...semifinals.map(match => ({ stage: 'sf', matchKey: match.matchKey, match })),
      { stage: 'final', matchKey: finalMatch.matchKey, match: finalMatch },
      { stage: 'third', matchKey: thirdPlaceMatch.matchKey, match: thirdPlaceMatch }
    ];

    setBracketScores(prev => {
      let changed = false;
      const next = { ...prev };

      candidates.forEach(({ stage, matchKey, match }) => {
        const actualScore = getActualBracketScore(stage, match);
        if (!actualScore) return;

        const current = next[matchKey] || {};
        const canApply = current.homeGoals === '' || current.homeGoals === undefined || current.awayGoals === '' || current.awayGoals === undefined || current.source === 'db';
        if (!canApply) return;

        const updated = { ...actualScore, source: 'db' };
        if (
          current.homeGoals !== updated.homeGoals ||
          current.awayGoals !== updated.awayGoals ||
          current.penaltyWinner !== updated.penaltyWinner ||
          current.source !== updated.source
        ) {
          next[matchKey] = updated;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [getActualBracketScore, roundOf32Matches, roundOf16, quarterfinals, semifinals, finalMatch, thirdPlaceMatch]);
  
  // Funkcja renderująca formatkę meczu dla drabinki wizualnej
  const renderMatchCard = (m) => {
    if (!m) return null;
    const score = bracketScores[m.matchKey] || { homeGoals: '', awayGoals: '', penaltyWinner: null };
    const isRemis = score.homeGoals !== '' && score.awayGoals !== '' && parseInt(score.homeGoals, 10) === parseInt(score.awayGoals, 10);
    const homeName = m.home?.teamName || m.placeholderHome || '---';
    const awayName = m.away?.teamName || m.placeholderAway || '---';
    const isDisabled = !m.home || !m.away;

    return (
      <Paper 
        key={m.matchKey}
        sx={{ 
          backgroundColor: '#2c2c2c', 
          px: 1, py: 0.5, 
          display: 'flex', flexDirection: 'column', 
          borderRadius: 1, border: '1px solid #3a3a3a',
          opacity: isDisabled ? 0.5 : 1,
          width: '100%',
          boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
          position: 'relative' // Przygotowanie pod ewentualne linie w przyszłości
        }}
      >
        {/* Gospodarz */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography 
            onClick={() => !isDisabled && isRemis && setPenaltyWinner(m.matchKey, 'home')}
            sx={{ 
              fontSize: '0.75rem', fontWeight: 500, color: '#fff', noWrap: true,
              cursor: (isRemis && !isDisabled) ? 'pointer' : 'default',
              textShadow: score.penaltyWinner === 'home' ? '0 0 5px #ffffff' : 'none'
            }}
          >
			{homeName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            <IconButton disabled={isDisabled} size="small" sx={{ p: 0.1, color: '#888' }} onClick={() => handleBracketGoal(m.matchKey, 'homeGoals', 'decrement')}><RemoveIcon sx={{ fontSize: '0.9rem' }} /></IconButton>
            <Typography sx={{ fontWeight: 'bold', width: '16px', textAlign: 'center', color: '#fff', fontSize: '0.8rem' }}>{score.homeGoals}</Typography>
            <IconButton disabled={isDisabled} size="small" sx={{ p: 0.1, color: '#fff' }} onClick={() => handleBracketGoal(m.matchKey, 'homeGoals', 'increment')}><AddIcon sx={{ fontSize: '0.9rem' }} /></IconButton>
          </Box>
        </Box>
        
        {/* Gość */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography 
            onClick={() => !isDisabled && isRemis && setPenaltyWinner(m.matchKey, 'away')}
            sx={{ 
              fontSize: '0.75rem', fontWeight: 500, color: '#fff', noWrap: true,
              cursor: (isRemis && !isDisabled) ? 'pointer' : 'default',
              textShadow: score.penaltyWinner === 'away' ? '0 0 5px #ffffff' : 'none'
            }}
          >
            {awayName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            <IconButton disabled={isDisabled} size="small" sx={{ p: 0.1, color: '#888' }} onClick={() => handleBracketGoal(m.matchKey, 'awayGoals', 'decrement')}><RemoveIcon sx={{ fontSize: '0.9rem' }} /></IconButton>
            <Typography sx={{ fontWeight: 'bold', width: '16px', textAlign: 'center', color: '#fff', fontSize: '0.8rem' }}>{score.awayGoals}</Typography>
            <IconButton disabled={isDisabled} size="small" sx={{ p: 0.1, color: '#fff' }} onClick={() => handleBracketGoal(m.matchKey, 'awayGoals', 'increment')}><AddIcon sx={{ fontSize: '0.9rem' }} /></IconButton>
          </Box>
        </Box>
      </Paper>
    );
  };
  
  const leftSide = {
    r32: [1, 4, 0, 2, 10, 11, 8, 9].map(i => roundOf32Matches[i]),
    r16: [0, 1, 4, 5].map(i => roundOf16[i]),
    qf: [0, 1].map(i => quarterfinals[i]),
    sf: [semifinals[0]]
  };

  const rightSide = {
    r32: [3, 5, 6, 7, 13, 15, 12, 14].map(i => roundOf32Matches[i]),
    r16: [2, 3, 6, 7].map(i => roundOf16[i]),
    qf: [2, 3].map(i => quarterfinals[i]),
    sf: [semifinals[1]]
  };

// Kolejność wizualna, aby pary naturalnie spływały do siebie od lewej do prawej (bez krzyżowania)
  const visualR32 = [1, 4, 0, 2, 10, 11, 8, 9, 3, 5, 6, 7, 13, 15, 12, 14].map(i => ({ ...roundOf32Matches[i], matchKey: `r32_${i}` }));
  const visualR16 = [0, 1, 4, 5, 2, 3, 6, 7].map(i => roundOf16[i]);
  const visualQF = [0, 1, 2, 3].map(i => quarterfinals[i]);
  const visualSF = [0, 1].map(i => semifinals[i]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
  if (error) return <Typography color="error" align="center">Błąd ładowania danych.</Typography>;

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 2 }}>
      <Helmet><title>Kalkulator MŚ | Symuliga</title></Helmet>

      <Typography variant="h5" component="h1" sx={{ mb: 1, fontWeight: 'bold', color: '#fff', textAlign: 'center' }}>
        Kalkulator - {leagueNameDisplay}
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: '#aaa', textAlign: 'center', maxWidth: 800, margin: '0 auto 24px auto' }}>
        Wpisz wyniki spotkań. Tabela i drabinka po prawej i dole obliczą się automatycznie w czasie rzeczywistym.
      </Typography>

      {/* SEKCJA WYBORU GRUPY - PEŁNA SZEROKOŚĆ EKRANU */}
      <Box sx={{ mb: 4 }}>
     
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.4 }}>
          {GROUPS.map(g => (
            <Button 
              key={g} 
              variant={selectedGroup === g ? "contained" : "outlined"} 
              onClick={() => setSelectedGroup(g)} 
              sx={{ minWidth: '40px', height: '32px', p: 0, borderRadius: 1, fontSize: '0.85rem' }}
            >
              {g}
            </Button>
          ))}
        </Box>
      </Box>

      {/* DWUKOLUMNOWY UKŁAD: MECZE PO LEWEJ, TABELA PO PRAWEJ */}
      <Grid container spacing={4} alignItems="flex-start">
        
        {/* LEWA STRONA: MECZE */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold', color: '#1976d2' }}>
            Mecze - Grupa {selectedGroup}
          </Typography>
          
          <Grid container spacing={1}>
            {Object.values(localMatches).filter(m => m.group === selectedGroup).map(m => {
              const isHighlighted = hoveredTableTeamId && (m.homeTeamId === hoveredTableTeamId || m.awayTeamId === hoveredTableTeamId);
              return (
                <Grid item xs={12} key={m.matchId}>
                  <Paper 
                    onMouseEnter={() => setHoveredMatchTeams([m.homeTeamId, m.awayTeamId])}
                    onMouseLeave={() => setHoveredMatchTeams(null)}
                    sx={{ 
                      backgroundColor: isHighlighted ? '#393939' : '#2c2c2c', 
                      px: 1.5, py: 0.4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 1.5, border: '1px solid #3a3a3a'
                    }}
                  >
                    <Typography 
                      
                      sx={{ flex: 1, fontSize: '0.82rem', fontWeight: 500, color: '#fff', pr: 1, noWrap: true, textDecoration: 'none'}}
                    >
                      {m.homeTeamName}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#1e1e1e', borderRadius: 1, p: '2px 4px' }}>
                      <IconButton size="small" sx={{ color: '#888', p: 0.2 }} onClick={() => handleGoalChange(m.matchId, 'homeGoals', 'decrement')}><RemoveIcon fontSize="inherit" /></IconButton>
                      <Typography sx={{ fontWeight: 'bold', width: '18px', textAlign: 'center', color: '#fff', fontSize: '0.85rem' }}>{m.homeGoals}</Typography>
                      <IconButton size="small" sx={{ color: '#fff', p: 0.2 }} onClick={() => handleGoalChange(m.matchId, 'homeGoals', 'increment')}><AddIcon fontSize="inherit" /></IconButton>
                      
                      <Typography sx={{ mx: 0.3, color: '#777', fontSize: '0.85rem', fontWeight: 'bold' }}>:</Typography>
                      
                      <IconButton size="small" sx={{ color: '#888', p: 0.2 }} onClick={() => handleGoalChange(m.matchId, 'awayGoals', 'decrement')}><RemoveIcon fontSize="inherit" /></IconButton>
                      <Typography sx={{ fontWeight: 'bold', width: '18px', textAlign: 'center', color: '#fff', fontSize: '0.85rem' }}>{m.awayGoals}</Typography>
                      <IconButton size="small" sx={{ color: '#fff', p: 0.2 }} onClick={() => handleGoalChange(m.matchId, 'awayGoals', 'increment')}><AddIcon fontSize="inherit" /></IconButton>
                    </Box>
                    
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pl: 1 }}>
                      <Typography 
                        
                        sx={{ textAlign: 'right', fontSize: '0.82rem', fontWeight: 500, color: '#fff', noWrap: true, mr: 1, textDecoration: 'none',}}
                      >
                        {m.awayTeamName}
                      </Typography>
                      <IconButton size="small" onClick={() => clearMatch(m.matchId)} sx={{ color: '#666', p: 0.2, '&:hover': { color: '#f44336' } }}>
                        <ClearIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" color="info" size="small" startIcon={<CasinoIcon />} onClick={() => randomizeMatches('group')} sx={{ textTransform: 'none' }}>
                Wylosuj mecze grupy {selectedGroup}
              </Button>
              <Button variant="outlined" color="secondary" size="small" startIcon={<CasinoIcon />} onClick={() => randomizeMatches('season')} sx={{ textTransform: 'none' }}>
                Wylosuj wszystkie mecze
              </Button>
            </Box>
            <Button variant="text" color="error" size="small" startIcon={<RestartAltIcon />} onClick={initializeMatches} sx={{ textTransform: 'none', fontWeight: 'bold', alignSelf: 'flex-start' }}>
              Resetuj wszystko do stanu z bazy
            </Button>
          </Box>
        </Grid>

        {/* PRAWA STRONA: TABELA LIVE */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold', color: '#1976d2' }}>
            Tabela - Grupa {selectedGroup}
          </Typography>

          <TableContainer component={Paper} sx={{ backgroundColor: '#1e1e1e', borderRadius: '8px', border: '1px solid #333' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#888', borderColor: '#333', fontSize: '0.75rem', width: '30px', textAlign: 'center' }}>Poz</TableCell>
                  <TableCell sx={{ color: '#888', borderColor: '#333', fontSize: '0.75rem' }}>Drużyna</TableCell>
                  <TableCell sx={{ color: '#888', borderColor: '#333', fontSize: '0.75rem', textAlign: 'center' }}>M</TableCell>
                  <TableCell sx={{ color: '#888', borderColor: '#333', fontSize: '0.75rem', textAlign: 'center' }}>Pkt</TableCell>
                  <TableCell sx={{ color: '#888', borderColor: '#333', fontSize: '0.75rem', textAlign: 'center' }}>Gole</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(groupTables[selectedGroup] || []).map((t, i) => {
                  const isPromotion = i < 2;
                  const isThird = i === 2;
                  const rowBgColor = isPromotion ? 'rgba(76, 175, 80, 0.15)' : isThird ? 'rgba(76, 175, 80, 0.05)' : 'transparent';
                  const isHoveredMatch = hoveredMatchTeams?.includes(t.teamId);

                  return (
                    <TableRow 
                      key={t.teamId}
                      onMouseEnter={() => setHoveredTableTeamId(t.teamId)}
                      onMouseLeave={() => setHoveredTableTeamId(null)}
                      sx={{ 
                        backgroundColor: isHoveredMatch ? '#393939' : rowBgColor,
                        '&:hover': { backgroundColor: isPromotion ? 'rgba(76, 175, 80, 0.25)' : '#333' },
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <TableCell sx={{ color: '#fff', borderColor: '#333', textAlign: 'center', fontWeight: 'bold' }}>{i + 1}</TableCell>
                      <TableCell sx={{ borderColor: '#333', fontWeight: 'normal' }}>                     
                          {t.teamName}
                      </TableCell>
                      <TableCell sx={{ color: '#fff', borderColor: '#333', textAlign: 'center' }}>{t.matchesPlayed}</TableCell>
                      <TableCell sx={{ color: '#fff', borderColor: '#333', textAlign: 'center', fontWeight: 'bold' }}>{t.points}</TableCell>
                      <TableCell sx={{ color: '#fff', borderColor: '#333', textAlign: 'center' }}>{t.goalsScored}:{t.goalsConceded}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

      </Grid>

      {/* ===================== WĄSKA TABELA 3. MIEJSC ===================== */}
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ color: '#fff', mb: 2, fontWeight: 'bold' }}>Tabela Trzecich Miejsc</Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: '#1e1e1e', borderRadius: '8px', border: '1px solid #333', width: 'max-content', maxWidth: '100%' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#888', borderColor: '#333', fontSize: '0.75rem', textAlign: 'center' }}>Poz</TableCell>
                <TableCell sx={{ color: '#888', borderColor: '#333', fontSize: '0.75rem', textAlign: 'center' }}>Gr</TableCell>
                <TableCell sx={{ color: '#888', borderColor: '#333', fontSize: '0.75rem' }}>Drużyna</TableCell>
                <TableCell sx={{ color: '#888', borderColor: '#333', fontSize: '0.75rem', textAlign: 'center' }}>Pkt</TableCell>
                <TableCell sx={{ color: '#888', borderColor: '#333', fontSize: '0.75rem', textAlign: 'center' }}>Bilans</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {thirdPlaceTable.map((t, i) => {
                const isPromo = i < 8;
                return (
                  <TableRow key={t.teamId} sx={{ backgroundColor: isPromo ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.1)' }}>
                    <TableCell sx={{ color: '#fff', borderColor: '#333', textAlign: 'center', fontWeight: 'bold' }}>{i + 1}</TableCell>
                    <TableCell sx={{ color: '#fff', borderColor: '#333', textAlign: 'center' }}>{t.group}</TableCell>
                    <TableCell sx={{ color: '#fff', borderColor: '#333', fontWeight: 'bold' }}>{t.teamName}</TableCell>
                    <TableCell sx={{ color: '#fff', borderColor: '#333', textAlign: 'center', fontWeight: 'bold' }}>{t.points}</TableCell>
                    <TableCell sx={{ color: '#fff', borderColor: '#333', textAlign: 'center' }}>
                      {t.goalsScored}:{t.goalsConceded} ({t.goalsScored - t.goalsConceded > 0 ? '+' : ''}{t.goalsScored - t.goalsConceded})
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

{/* ===================== WIZUALNA DRABINKA PUCHAROWA (LEWA -> PRAWA) ===================== */}
      <Box sx={{ mt: 8, mb: 10, overflowX: 'auto', width: '100%', pb: 0 }}>
        <Typography variant="h5" sx={{ color: '#fff', mb: 4, fontWeight: 'bold', textAlign: 'center' }}>
          Drabinka Fazy Pucharowej
        </Typography>

        {/* minWidth: 1400 wymusza pasek przewijania poziomego na mniejszych ekranach, zapobiegając zgnieceniu */}
        <Box sx={{ 
			display: 'flex', 
			minWidth: 1000, // Zmniejszamy minWidth, jeśli kolumny będą węższe
			gap: 1, 
			px: 2,
			mx: 'auto', // Wyśrodkowanie całej drabinki na stronie
			width: 'fit-content' // Ograniczenie szerokości do zawartości
		}}>
		  
		  {/* Kolumny - teraz każda będzie miała max 200px szerokości */}
		  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', flex: 1, gap: 1, maxWidth: 200 }}>
			{visualR32.map(renderMatchCard)}
		  </Box>

		  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', flex: 1, gap: 1, maxWidth: 200 }}>
			{visualR16.map(renderMatchCard)}
		  </Box>

		  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', flex: 1, gap: 1, maxWidth: 200 }}>
			{visualQF.map(renderMatchCard)}
		  </Box>

		  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', flex: 1, gap: 1, maxWidth: 200 }}>
			{visualSF.map(renderMatchCard)}
		  </Box>

		  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, gap: 8, maxWidth: 200 }}>
            <Box>
              <Typography sx={{ color: '#ffd700', fontWeight: 'bold', mb: 1.5, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 1 }}>Finał</Typography>
              {renderMatchCard(finalMatch)}
            </Box>
            <Box>
              <Typography sx={{ color: '#aaa', fontSize: '0.8rem', mb: 1.5, textAlign: 'center', textTransform: 'uppercase' }}>Mecz o 3. miejsce</Typography>
              {renderMatchCard(thirdPlaceMatch)}
            </Box>
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

export default WcStandingsCalculator;
